// --- Dependencies -------------------------------------------------------------------------------
// --- Our Modules --------------------------------------------------------------------------------
import type { Lang } from "@/hooks/useLang/types";
import type { TyEditorCategory, TyEditorDiscount, TyEditorPrice, TyEditorProduct } from "@/types";
import { hasAnyLangValue, readLangs, writeLangs } from "@/utils";
import type { TyLangValues } from "@/utils";

// --- Types --------------------------------------------------------------------------------------

/**
 * A grid row. `key` is local and stable for React and for keyboard navigation; `_id` is absent until
 * the row has been saved once, which is exactly what tells the bulk endpoints to insert rather than
 * update. `saved` is the last state the server confirmed, so "dirty" is a comparison, never a flag
 * that can drift out of sync with the data.
 */
export type TyRow<T> = {
    key: string;
    _id?: string;
    draft: T;
    saved: T | null;
};

export type TyCategoryDraft = {
    name: TyLangValues;
    sort: number;
    productsCount: number;
};

export type TyProductDraft = {
    name: TyLangValues;
    desc: TyLangValues;
    category: string[];

    /** The full tier list, edited in a popup — a product can carry any number of them. */
    prices: TyEditorPrice[];

    discount: TyEditorDiscount;
    active: boolean;
    hidden: boolean;
    picture: string | null;
};

// --- Helpers ------------------------------------------------------------------------------------

let keyCounter = 0;

export const nextRowKey = (): string => `row-${++keyCounter}`;

/** Blank means "nothing typed yet" — the state a row is in the instant it is added. */
export const isCategoryRowBlank = (row: TyRow<TyCategoryDraft>): boolean => !row._id && !hasAnyLangValue(row.draft.name);

export const isProductRowBlank = (row: TyRow<TyProductDraft>): boolean =>
    !row._id && !hasAnyLangValue(row.draft.name) && !hasAnyLangValue(row.draft.desc) && row.draft.prices.length === 0;

export const isRowDirty = <T>(row: TyRow<T>): boolean => row.saved === null || JSON.stringify(row.draft) !== JSON.stringify(row.saved);

// --- Mapping ------------------------------------------------------------------------------------

export const toCategoryRow = (category: TyEditorCategory, languages: Lang[]): TyRow<TyCategoryDraft> => {
    const draft: TyCategoryDraft = {
        name: readLangs(category.name, languages),
        sort: category.sort ?? 0,
        productsCount: category.products?.length ?? 0,
    };
    return { key: nextRowKey(), _id: category._id, draft, saved: { ...draft } };
};

export const toProductRow = (product: TyEditorProduct, languages: Lang[]): TyRow<TyProductDraft> => {
    const draft: TyProductDraft = {
        name: readLangs(product.name, languages),
        desc: readLangs(product.description, languages),
        category: [...(product.category ?? [])],
        prices: (product.prices ?? []).map((price) => ({ ...price, label: [...(price.label ?? [])] })),
        discount: product.discount ? { ...product.discount } : null,
        active: product.active,
        hidden: product.hidden,
        picture: product.pictures?.[0] ?? null,
    };
    return { key: nextRowKey(), _id: product._id, draft, saved: structuredClone(draft) };
};

export const emptyCategoryDraft = (sort: number): TyCategoryDraft => ({ name: {}, sort, productsCount: 0 });

export const emptyProductDraft = (category: string[]): TyProductDraft => ({
    name: {},
    desc: {},
    category,
    prices: [],
    discount: null,
    active: true,
    hidden: false,
    picture: null,
});

// --- Payloads -----------------------------------------------------------------------------------

export const categoryPayload = (row: TyRow<TyCategoryDraft>, languages: Lang[], defaultLang: Lang) => ({
    ...(row._id ? { _id: row._id } : {}),
    name: writeLangs(row.draft.name, languages, defaultLang),
    sort: row.draft.sort,
});

/** Every field the grid owns is sent as-is; pictures are the one thing the editor never carries. */
export const productPayload = (row: TyRow<TyProductDraft>, languages: Lang[], defaultLang: Lang) => ({
    ...(row._id ? { _id: row._id } : {}),
    name: writeLangs(row.draft.name, languages, defaultLang),
    description: writeLangs(row.draft.desc, languages, defaultLang),
    prices: row.draft.prices,
    discount: row.draft.discount,
    category: row.draft.category,
    active: row.draft.active,
    hidden: row.draft.hidden,
});
